export interface Experience {
  company: string;
  role: string;
  period: string;
  location: string;
  projects: {
    name: string;
    description: string;
    teamSize: number;
    technologies: string[];
    responsibilities: string[];
  }[];
}

export interface Education {
  institution: string;
  degree: string;
  period: string;
}

export interface Certificate {
  name: string;
  status: 'certified' | 'researching';
}

export interface TeamMember {
  id: string;
  name: string;
  nameVi?: string;
  role: string;
  roleVi?: string;
  email: string;
  location: string;
  locationVi?: string;
  avatar: string;
  bio: string;
  bioVi?: string;
  github?: string;
  linkedin?: string;
  skills: string[];
  skillCategories: { category: string; categoryVi?: string; items: string[] }[];
  experience: Experience[];
  experienceVi?: Experience[];
  education: Education[];
  certificates: Certificate[];
  languages: { name: string; level: string; levelVi?: string; description: string; descriptionVi?: string }[];
  softSkills?: string[];
  softSkillsVi?: string[];
  activities?: string[];
  activitiesVi?: string[];
  cvFile: string;
  isPlaceholder?: boolean;
}

export const members: TeamMember[] = [
  {
    id: 'thanh-dm',
    name: 'Thanh D M',
    nameVi: 'Đặng Minh Thành',
    role: 'Developer',
    roleVi: 'Lập trình viên',
    email: 'dangminhthanh.dut@gmail.com',
    location: 'Da Nang, Viet Nam',
    locationVi: 'Đà Nẵng, Việt Nam',
    avatar: '[AVATAR_MEMBER_1]',
    bio: 'A Backend Engineer progressing toward becoming a technical leader capable of guiding teams through complex client challenges. Built expertise in cloud infrastructure and enterprise-grade API development for Japanese clients. Continuously explores emerging technologies and AI-assisted development tools to enhance code quality and team productivity.',
    bioVi: 'Kỹ sư Backend đang phát triển hướng tới vị trí lãnh đạo kỹ thuật, có khả năng dẫn dắt đội ngũ vượt qua các thách thức phức tạp từ khách hàng. Xây dựng chuyên môn về hạ tầng đám mây và phát triển API cấp doanh nghiệp cho khách hàng Nhật Bản. Không ngừng khám phá công nghệ mới và công cụ phát triển hỗ trợ AI để nâng cao chất lượng code và năng suất đội ngũ.',
    github: 'https://github.com/thanh-dm',
    linkedin: 'https://linkedin.com/in/thanh-dm',
    skills: ['Node.js', 'Go', 'Python', 'TypeScript', 'C#', 'NestJS', 'Express', 'HapiJS', 'Django', 'ASP.NET', 'Astro', 'AWS', 'Azure', 'Google Cloud', 'Docker', 'PostgreSQL', 'MongoDB', 'SQL Server', 'GitHub Actions', 'Serverless', 'TypeORM', 'Stripe', 'Agile/Scrum', 'Clean Architecture', 'Code Review'],
    skillCategories: [
      { category: 'Languages', categoryVi: 'Ngôn ngữ', items: ['Node.js', 'Go', 'Python', 'TypeScript', 'C#'] },
      { category: 'Frameworks', categoryVi: 'Framework', items: ['NestJS', 'Express', 'HapiJS', 'Django', 'ASP.NET', 'Astro'] },
      { category: 'Cloud & DevOps', items: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'GitHub Actions', 'Serverless', 'GitHub Submodule'] },
      { category: 'Databases', categoryVi: 'Cơ sở dữ liệu', items: ['PostgreSQL', 'MongoDB', 'SQL Server'] },
      { category: 'Other', categoryVi: 'Khác', items: ['TypeORM', 'Stripe', 'Momo', 'Agile/Scrum', 'Clean Architecture', 'Code Review'] },
    ],
    experience: [
      {
        company: 'Monstarlab VN',
        role: 'Backend Engineer',
        period: '03/2023 – Present',
        location: 'Da Nang',
        projects: [
          {
            name: 'Enterprise Goal Management System',
            description: 'Enterprise goal management system with multi-level organizational hierarchy and role-based access control. Built for Japanese market with fiscal year support and complex authorization.',
            teamSize: 2,
            technologies: ['Node.js', 'NestJS', 'TypeORM', 'PostgreSQL', 'AWS (ECS Fargate, RDS, Cognito, S3)', 'Docker', 'GitHub Actions'],
            responsibilities: ['Analyze requirements and break down into tasks', 'Implement REST APIs and database design', 'Code review and ensure quality standards', 'Track team progress and delivery'],
          },
          {
            name: 'E-commerce Management System (Phase 2)',
            description: 'Sales management system, online design and printing platform for banners and labels. Customers can customize designs and place orders.',
            teamSize: 3,
            technologies: ['Go', 'AWS (SQS, Lambda, S3, CodePipeline, CloudWatch)', 'PostgreSQL', 'Serverless Framework'],
            responsibilities: ['Analyze requirements and break down', 'Code review', 'Track team progress'],
          },
          {
            name: 'Freelance Projects',
            description: 'Various small-scale projects including feature modules and landing pages for startup businesses.',
            teamSize: 1,
            technologies: ['Django', 'ASP.NET', 'Astro', 'MongoDB', 'SQL Server'],
            responsibilities: ['Full-stack development', 'Client communication', 'Delivery'],
          },
        ],
      },
      {
        company: 'Monstarlab VN',
        role: 'Backend Engineer',
        period: '01/2022 – 03/2023',
        location: 'Da Nang',
        projects: [
          {
            name: 'E-commerce Management System (Phase 1)',
            description: 'Banner, label and sales management platform with printing and notification features.',
            teamSize: 4,
            technologies: ['Go', 'AWS (SQS, Lambda, RDS, S3, CodePipeline, CloudWatch)', 'PostgreSQL', 'Serverless Framework'],
            responsibilities: ['Receive task assignments and technical specifications', 'Implement REST APIs and utility features (printing, SMS notifications, mailer)', 'Self-testing and bug fixing'],
          },
          {
            name: 'Healthcare Support System',
            description: 'Healthcare platform for mother and baby wellness tracking, expert appointment scheduling, and health consultation services.',
            teamSize: 4,
            technologies: ['NestJS', 'Azure (App Service, Functions, Communication Services, Blob Storage)', 'PostgreSQL'],
            responsibilities: ['Receive task assignments and technical specifications', 'Implement REST APIs and utility features (SMS notifications, mailer, appointment scheduling)', 'Self-testing and bug fixing'],
          },
        ],
      },
      {
        company: 'Freelance',
        role: 'Software Engineer',
        period: '12/2019 – 01/2022',
        location: 'Da Nang',
        projects: [
          {
            name: 'Various Client Projects',
            description: 'Built technical foundation working on diverse projects. Worked with mentors and clients on outsourced work and independent product development.',
            teamSize: 2,
            technologies: ['Python (Django)', 'Node.js (Express, HapiJS)', 'MongoDB', 'AWS (ECS, RDS, Cognito, S3, CodePipeline)', 'Stripe', 'Momo'],
            responsibilities: ['Full-stack development', 'Direct client collaboration', 'Integration of third-party services'],
          },
        ],
      },
    ],
    education: [
      {
        institution: 'DUT - Da Nang University of Technology',
        degree: 'Information Technology',
        period: '2017 – 2022',
      },
    ],
    certificates: [
      { name: 'AWS Certified Solutions Architect – Associate', status: 'researching' },
      { name: 'Agentic Coding with AI-powered development tools', status: 'researching' },
      { name: 'Scrum Master 1', status: 'certified' },
      { name: 'Japanese N4 (JLPT)', status: 'certified' },
    ],
    languages: [
      { name: 'English', level: 'Intermediate', levelVi: 'Trung cấp', description: 'Proficient in reading technical documentation, researching, writing specifications', descriptionVi: 'Thành thạo đọc tài liệu kỹ thuật, nghiên cứu, viết đặc tả' },
      { name: 'Japanese', level: 'Intermediate reading', levelVi: 'Đọc trung cấp', description: 'Comprehension for technical documents, currently improving', descriptionVi: 'Hiểu tài liệu kỹ thuật, đang cải thiện' },
      { name: 'Vietnamese', level: 'Native', levelVi: 'Bản ngữ', description: 'Native speaker', descriptionVi: 'Người bản ngữ' },
    ],
    activities: [
      'Passionate about sports, particularly badminton (active tournament participant)',
      'Gaming: Valorant, League of Legends, Teamfight Tactics',
      'Enjoys singing with friends',
    ],
    activitiesVi: [
      'Đam mê thể thao, đặc biệt cầu lông (thường xuyên tham gia giải đấu)',
      'Gaming: Valorant, League of Legends, Teamfight Tactics',
      'Thích hát karaoke cùng bạn bè',
    ],
    cvFile: '[CV_FILE_MEMBER_1]',
  },
  {
    id: 'phat-tvp',
    name: 'Phat T V',
    role: 'Website Designer & Builder',
    roleVi: 'Thiết kế & Xây dựng Website',
    email: 'vanphat2k1@gmail.com',
    location: 'Ho Chi Minh, Vietnam',
    locationVi: 'TP. Hồ Chí Minh, Việt Nam',
    avatar: '[AVATAR_MEMBER_2]',
    bio: 'Website Designer with hands-on experience in building, optimizing, and maintaining high-performing websites using WordPress and modern website builders. Skilled in UI/UX design, responsive layouts, SEO optimization, and performance tuning. Proven ability to manage multiple client projects simultaneously and deliver user-centered digital experiences aligned with business goals.',
    bioVi: 'Nhà thiết kế Website với kinh nghiệm thực tế trong xây dựng, tối ưu hóa và bảo trì các website hiệu suất cao sử dụng WordPress và các công cụ xây dựng web hiện đại. Thành thạo thiết kế UI/UX, bố cục responsive, tối ưu SEO và hiệu năng. Có khả năng quản lý đồng thời nhiều dự án khách hàng và mang đến trải nghiệm số lấy người dùng làm trung tâm, phù hợp với mục tiêu kinh doanh.',
    skills: ['PHP', 'HTML/CSS', 'JavaScript', 'jQuery', 'WordPress', 'Duda', 'Wix', 'Squarespace', 'GoHighLevel', 'DIVI', 'Flatsome', 'Figma', 'Adobe Photoshop', 'Adobe Illustrator', 'SEO Optimization', 'Website Auditor', 'Bootstrap', 'Responsive Design', 'UI/UX Design', 'Performance Optimization'],
    skillCategories: [
      { category: 'Languages', categoryVi: 'Ngôn ngữ', items: ['PHP', 'HTML/CSS', 'JavaScript'] },
      { category: 'Frameworks', categoryVi: 'Framework', items: ['jQuery', 'Bootstrap'] },
      { category: 'CMS/Builders', categoryVi: 'CMS/Công cụ xây dựng', items: ['WordPress', 'Duda', 'Wix', 'Squarespace', 'GoHighLevel'] },
      { category: 'WordPress Themes', categoryVi: 'Theme WordPress', items: ['DIVI', 'Flatsome'] },
      { category: 'Design Tools', categoryVi: 'Công cụ thiết kế', items: ['Figma', 'Adobe Photoshop', 'Adobe Illustrator'] },
      { category: 'Other', categoryVi: 'Khác', items: ['SEO Optimization', 'Website Auditor', 'Responsive Design', 'UI/UX Design', 'Performance Optimization'] },
    ],
    experience: [
      {
        company: 'SEO Navigator',
        role: 'Website Designer',
        period: '12/2024 – 12/2025',
        location: 'Remote',
        projects: [
          {
            name: 'Multi-industry Website Design & Management',
            description: 'Designed and delivered full websites and landing pages tailored to diverse industries and aligned with client business goals. Managed, updated, and optimized multiple client websites simultaneously.',
            teamSize: 1,
            technologies: ['Duda', 'WordPress', 'Wix', 'Squarespace', 'GoHighLevel', 'Adobe Photoshop', 'Illustrator', 'Figma'],
            responsibilities: [
              'Designed and delivered full websites and landing pages tailored to diverse industries',
              'Managed, updated, and optimized multiple client websites simultaneously',
              'Collaborated directly with clients to gather requirements and implement feedback',
              'Customized templates, integrated third-party tools, and utilized drag-and-drop builders',
              'Edited website structures, layouts, and functions to support SEO and digital marketing',
            ],
          },
        ],
      },
      {
        company: 'Global Liaison',
        role: 'Website Designer',
        period: '09/2023 – 12/2024',
        location: 'Remote',
        projects: [
          {
            name: 'Custom WordPress Website Development',
            description: 'Designed and built custom websites using WordPress (DIVI), incorporating unique visual effects, interactive features, and promotional elements based on client requirements.',
            teamSize: 2,
            technologies: ['PHP', 'HTML/CSS', 'JavaScript', 'WordPress (DIVI)', 'Adobe Photoshop', 'Illustrator', 'Figma'],
            responsibilities: [
              'Designed and built custom websites using WordPress (DIVI) with interactive features',
              'Collaborated with clients and internal teams to develop responsive, SEO-friendly websites',
              'Applied PHP, HTML/CSS, JavaScript, and Figma to customize UI/UX',
              'Optimized website performance and mobile compatibility across modern browsers',
            ],
          },
        ],
      },
      {
        company: 'Global Branding Online',
        role: 'Web Programming',
        period: '03/2023 – 06/2023',
        location: 'Ho Chi Minh',
        projects: [
          {
            name: 'Website SEO & Performance Optimization',
            description: 'Improved website structure and content to comply with Google SEO standards, enhancing search visibility and overall performance.',
            teamSize: 2,
            technologies: ['PHP', 'HTML/CSS', 'JavaScript', 'WordPress (Flatsome)', 'jQuery', 'Website Auditor'],
            responsibilities: [
              'Improved website structure and content to comply with Google SEO standards',
              'Edited layouts following UI/UX best practices and SEO guidelines',
              'Resolved website security issues, malware infections, and performed speed optimization',
            ],
          },
        ],
      },
      {
        company: 'Freelance',
        role: 'WordPress Developer',
        period: '08/2023 – 01/2026',
        location: 'Remote',
        projects: [
          {
            name: 'Various Client Websites',
            description: 'Designed, built, and maintained multiple client websites across various industries, delivering complete end-to-end website solutions including hosting setup, deployment, and domain configuration.',
            teamSize: 1,
            technologies: ['WordPress (Flatsome)', 'HTML/CSS', 'JavaScript', 'Bootstrap', 'Figma', 'Adobe Photoshop'],
            responsibilities: [
              'Designed, built, and maintained multiple client websites across various industries',
              'Developed landing pages, blog, contact, and about sections based on client requirements',
              'Set up hosting environments, deployed websites, and handled domain configuration',
              'Optimized website performance, security, and responsiveness across all devices',
              'Troubleshot technical issues, resolved malware problems, and improved website speed',
            ],
          },
        ],
      },
    ],
    experienceVi: [
      {
        company: 'SEO Navigator',
        role: 'Thiết kế Website',
        period: '12/2024 – 12/2025',
        location: 'Từ xa',
        projects: [
          {
            name: 'Thiết kế & Quản lý Website đa ngành',
            description: 'Thiết kế và triển khai website hoàn chỉnh và landing page cho nhiều ngành nghề khác nhau, phù hợp với mục tiêu kinh doanh của khách hàng.',
            teamSize: 1,
            technologies: ['Duda', 'WordPress', 'Wix', 'Squarespace', 'GoHighLevel', 'Adobe Photoshop', 'Illustrator', 'Figma'],
            responsibilities: [
              'Thiết kế và triển khai website và landing page cho nhiều ngành nghề',
              'Quản lý, cập nhật và tối ưu đồng thời nhiều website khách hàng',
              'Làm việc trực tiếp với khách hàng để thu thập yêu cầu và triển khai phản hồi',
              'Tùy chỉnh template, tích hợp công cụ bên thứ ba, sử dụng trình kéo-thả',
              'Chỉnh sửa cấu trúc, bố cục website để hỗ trợ SEO và digital marketing',
            ],
          },
        ],
      },
      {
        company: 'Global Liaison',
        role: 'Thiết kế Website',
        period: '09/2023 – 12/2024',
        location: 'Từ xa',
        projects: [
          {
            name: 'Phát triển Website WordPress tùy chỉnh',
            description: 'Thiết kế và xây dựng website tùy chỉnh bằng WordPress (DIVI), tích hợp hiệu ứng hình ảnh, tính năng tương tác theo yêu cầu khách hàng.',
            teamSize: 2,
            technologies: ['PHP', 'HTML/CSS', 'JavaScript', 'WordPress (DIVI)', 'Adobe Photoshop', 'Illustrator', 'Figma'],
            responsibilities: [
              'Thiết kế và xây dựng website tùy chỉnh bằng WordPress (DIVI) với tính năng tương tác',
              'Hợp tác với khách hàng và đội ngũ nội bộ phát triển website responsive, thân thiện SEO',
              'Áp dụng PHP, HTML/CSS, JavaScript và Figma để tùy chỉnh UI/UX',
              'Tối ưu hiệu năng website và tương thích di động trên các trình duyệt hiện đại',
            ],
          },
        ],
      },
      {
        company: 'Global Branding Online',
        role: 'Lập trình Web',
        period: '03/2023 – 06/2023',
        location: 'TP. Hồ Chí Minh',
        projects: [
          {
            name: 'Tối ưu SEO & Hiệu năng Website',
            description: 'Cải thiện cấu trúc và nội dung website tuân thủ tiêu chuẩn Google SEO, nâng cao khả năng hiển thị tìm kiếm và hiệu suất tổng thể.',
            teamSize: 2,
            technologies: ['PHP', 'HTML/CSS', 'JavaScript', 'WordPress (Flatsome)', 'jQuery', 'Website Auditor'],
            responsibilities: [
              'Cải thiện cấu trúc và nội dung website tuân thủ tiêu chuẩn Google SEO',
              'Chỉnh sửa bố cục theo UI/UX best practices và hướng dẫn SEO',
              'Xử lý vấn đề bảo mật, malware và tối ưu tốc độ website',
            ],
          },
        ],
      },
      {
        company: 'Freelance',
        role: 'WordPress Developer',
        period: '08/2023 – 01/2026',
        location: 'Từ xa',
        projects: [
          {
            name: 'Các dự án Website khách hàng',
            description: 'Thiết kế, xây dựng và bảo trì nhiều website khách hàng đa ngành, cung cấp giải pháp website toàn diện từ đầu đến cuối.',
            teamSize: 1,
            technologies: ['WordPress (Flatsome)', 'HTML/CSS', 'JavaScript', 'Bootstrap', 'Figma', 'Adobe Photoshop'],
            responsibilities: [
              'Thiết kế, xây dựng và bảo trì nhiều website khách hàng đa ngành',
              'Phát triển landing page, blog, liên hệ và giới thiệu theo yêu cầu khách hàng',
              'Thiết lập hosting, triển khai website và cấu hình tên miền',
              'Tối ưu hiệu năng, bảo mật và responsive trên mọi thiết bị',
              'Xử lý sự cố kỹ thuật, malware và cải thiện tốc độ website',
            ],
          },
        ],
      },
    ],
    education: [
      {
        institution: 'Hutech University',
        degree: 'Information Technology',
        period: '2019 – 2023',
      },
    ],
    certificates: [],
    languages: [
      { name: 'Vietnamese', level: 'Native', levelVi: 'Bản ngữ', description: 'Native speaker', descriptionVi: 'Người bản ngữ' },
      { name: 'English', level: 'B1 - Working proficiency', levelVi: 'B1 - Trình độ làm việc', description: 'Working proficiency for client communication and technical documentation', descriptionVi: 'Đủ năng lực giao tiếp với khách hàng và viết tài liệu kỹ thuật' },
    ],
    cvFile: '[CV_FILE_MEMBER_2]',
  },
  {
    id: 'nghia-lv',
    name: 'Nghia L V',
    nameVi: 'Lê Văn Nghĩa',
    role: 'Developer',
    roleVi: 'Lập trình viên',
    email: 'lvnghia46@gmail.com',
    location: 'Da Nang, Viet Nam',
    locationVi: 'Đà Nẵng, Việt Nam',
    avatar: '[AVATAR_MEMBER_3]',
    bio: 'A Backend Engineer with a strong interest in technology and problem-solving. Possesses excellent self-study abilities and extensive experience in the ERP field with a thorough understanding of systems required to operate a company. Skilled in building scalable backend architectures and collaborating effectively in team environments.',
    bioVi: 'Kỹ sư Backend với niềm đam mê công nghệ và giải quyết vấn đề. Có khả năng tự học tốt và kinh nghiệm phong phú trong lĩnh vực ERP, hiểu biết sâu sắc về các hệ thống cần thiết để vận hành một công ty. Thành thạo xây dựng kiến trúc backend có khả năng mở rộng và hợp tác hiệu quả trong môi trường nhóm.',
    skills: ['Node.js', 'TypeScript', 'Coldfusion', 'Python', 'HTML/CSS', 'JavaScript', 'React', 'NestJS', 'Express', 'PostgreSQL', 'MongoDB', 'SQL Server', 'TypeORM', 'GitHub Actions', 'Agile/Scrum', 'Docker'],
    skillCategories: [
      { category: 'Languages', categoryVi: 'Ngôn ngữ', items: ['Node.js', 'TypeScript', 'Coldfusion', 'Python', 'HTML/CSS', 'JavaScript'] },
      { category: 'Frameworks', categoryVi: 'Framework', items: ['NestJS', 'Express', 'React', 'TypeORM'] },
      { category: 'Databases', categoryVi: 'Cơ sở dữ liệu', items: ['PostgreSQL', 'MongoDB', 'SQL Server'] },
      { category: 'Other', categoryVi: 'Khác', items: ['GitHub Actions', 'Agile/Scrum', 'Docker'] },
    ],
    experience: [
      {
        company: 'Globe3 ERP Vietnam',
        role: 'Engineer',
        period: '06/2023 – Present',
        location: 'Vietnam',
        projects: [
          {
            name: 'ERP System',
            description: 'Comprehensive ERP system providing tools to support all activities of a company. Serving more than 150 clients worldwide.',
            teamSize: 20,
            technologies: ['HTML', 'CSS', 'JavaScript', 'Coldfusion', 'SQL'],
            responsibilities: ['Coding', 'System maintenance', 'UI Design', 'System & Module Development'],
          },
        ],
      },
      {
        company: 'Hybrid-Technologies',
        role: 'Fresher',
        period: '06/2022 – 12/2022',
        location: 'Vietnam',
        projects: [
          {
            name: 'Device Management',
            description: 'Build API Back-end for device management system.',
            teamSize: 6,
            technologies: ['Node.js', 'Docker', 'MySQL', 'TypeORM', 'Express', 'OvernightJs'],
            responsibilities: ['Coding', 'Build architecture'],
          },
          {
            name: 'Log Off System',
            description: 'System providing functions related to logging day off for employees in a company, with log day off process and related functionality for actors.',
            teamSize: 3,
            technologies: ['React', 'Node.js', 'MongoDB', 'Docker'],
            responsibilities: ['Coding', 'Build architecture'],
          },
        ],
      },
    ],
    experienceVi: [
      {
        company: 'Globe3 ERP Vietnam',
        role: 'Kỹ sư',
        period: '06/2023 – Hiện tại',
        location: 'Việt Nam',
        projects: [
          {
            name: 'Hệ thống ERP',
            description: 'Hệ thống ERP toàn diện cung cấp công cụ hỗ trợ mọi hoạt động của công ty. Phục vụ hơn 150 khách hàng trên toàn thế giới.',
            teamSize: 20,
            technologies: ['HTML', 'CSS', 'JavaScript', 'Coldfusion', 'SQL'],
            responsibilities: ['Lập trình', 'Bảo trì hệ thống', 'Thiết kế UI', 'Phát triển hệ thống & module'],
          },
        ],
      },
      {
        company: 'Hybrid-Technologies',
        role: 'Fresher',
        period: '06/2022 – 12/2022',
        location: 'Việt Nam',
        projects: [
          {
            name: 'Quản lý Thiết bị',
            description: 'Xây dựng API Back-end cho hệ thống quản lý thiết bị.',
            teamSize: 6,
            technologies: ['Node.js', 'Docker', 'MySQL', 'TypeORM', 'Express', 'OvernightJs'],
            responsibilities: ['Lập trình', 'Xây dựng kiến trúc'],
          },
          {
            name: 'Hệ thống Nghỉ phép',
            description: 'Hệ thống cung cấp các chức năng liên quan đến đăng ký nghỉ phép cho nhân viên trong công ty.',
            teamSize: 3,
            technologies: ['React', 'Node.js', 'MongoDB', 'Docker'],
            responsibilities: ['Lập trình', 'Xây dựng kiến trúc'],
          },
        ],
      },
    ],
    education: [
      {
        institution: 'Da Nang University of Technology',
        degree: 'Information Technology',
        period: '2018 – 2023',
      },
    ],
    certificates: [
      { name: 'Best Employee of Q1 & Q2 2024 — Globe3 ERP Vietnam', status: 'certified' },
    ],
    languages: [
      { name: 'Vietnamese', level: 'Native', levelVi: 'Bản ngữ', description: 'Native speaker', descriptionVi: 'Người bản ngữ' },
      { name: 'English', level: 'Working proficiency', levelVi: 'Trình độ làm việc', description: 'Proficient in reading technical documentation and team communication', descriptionVi: 'Thành thạo đọc tài liệu kỹ thuật và giao tiếp nhóm' },
    ],
    softSkills: ['Good communication', 'Teamwork', 'Honesty', 'Self-motivation / Initiative', 'Creativity, Flexibility'],
    softSkillsVi: ['Giao tiếp tốt', 'Làm việc nhóm', 'Trung thực', 'Tự chủ / Chủ động', 'Sáng tạo, Linh hoạt'],
    activities: [
      'Self-study and exploring new technologies',
      'Problem-solving and algorithmic challenges',
    ],
    activitiesVi: [
      'Tự học và khám phá công nghệ mới',
      'Giải quyết vấn đề và thử thách thuật toán',
    ],
    cvFile: '[CV_FILE_MEMBER_3]',
  },
  {
    id: 'nguyen-lmt',
    name: 'Nguyen L M T',
    nameVi: 'Nguyên Lê Minh Trịnh',
    role: 'Developer',
    roleVi: 'Lập trình viên',
    email: 'leminhtrinhnguyenr@gmail.com',
    location: 'Da Nang, Vietnam',
    locationVi: 'Đà Nẵng, Việt Nam',
    avatar: '[AVATAR_MEMBER_4]',
    bio: 'Results-driven Fullstack Software Engineer with 6+ years of experience designing, building, and scaling web applications across diverse industries including aviation, real estate, social networking, and IoT. Proven expertise in microservices architecture, cloud-native development on AWS, and performance optimization. Adept at leading cross-functional teams, mentoring junior developers, and delivering high-quality solutions under tight deadlines. Passionate about clean code, TDD, and leveraging AI-powered tools to accelerate development workflows.',
    bioVi: 'Kỹ sư phần mềm Fullstack có hơn 6 năm kinh nghiệm thiết kế, xây dựng và mở rộng ứng dụng web đa ngành bao gồm hàng không, bất động sản, mạng xã hội và IoT. Chuyên gia về kiến trúc microservices, phát triển cloud-native trên AWS, và tối ưu hiệu năng. Có khả năng dẫn dắt đội ngũ đa chức năng, hướng dẫn lập trình viên junior, và cung cấp giải pháp chất lượng cao dưới áp lực thời gian. Đam mê clean code, TDD, và tận dụng công cụ AI để tăng tốc quy trình phát triển.',
    github: 'https://github.com/lmtn2000',
    linkedin: 'https://linkedin.com/in/minhtrinhnguyenle-se',
    skills: ['React', 'Next.js', 'Vue.js', 'Node.js', 'Python', 'Angular', 'Tailwind CSS', 'NestJS', 'FastAPI', 'React Native', 'PostgreSQL', 'MySQL', 'DynamoDB', 'AWS', 'Docker', 'LocalStack', 'Agile/Scrum', 'Microservices', 'TDD', 'REST API', 'Clean Architecture', 'Code Review'],
    skillCategories: [
      { category: 'Languages (Frontend)', categoryVi: 'Ngôn ngữ (Frontend)', items: ['React', 'Next.js', 'Vue.js'] },
      { category: 'Languages (Backend)', categoryVi: 'Ngôn ngữ (Backend)', items: ['Node.js', 'Python'] },
      { category: 'Frameworks', categoryVi: 'Framework', items: ['Angular', 'Tailwind CSS', 'NestJS', 'FastAPI', 'React Native'] },
      { category: 'Databases', categoryVi: 'Cơ sở dữ liệu', items: ['PostgreSQL', 'MySQL', 'DynamoDB'] },
      { category: 'Cloud & DevOps', items: ['AWS (ECS, Lambda, S3)', 'Docker', 'LocalStack', 'CI/CD'] },
      { category: 'Other', categoryVi: 'Khác', items: ['Agile/Scrum', 'Microservices', 'TDD', 'REST API', 'Clean Architecture', 'Code Review'] },
    ],
    experience: [
      {
        company: 'VNEXT Software',
        role: 'Full Stack Developer | Team Lead',
        period: '11/2023 – Present',
        location: 'Da Nang',
        projects: [
          {
            name: 'TaaS (Travel as a Service) — Major Airline Platform',
            description: 'Led a team of 5 developers to build and maintain a travel booking platform for a major airline, handling hotel reservations and rental car services at scale with high availability.',
            teamSize: 5,
            technologies: ['Next.js', 'NestJS', 'Python', 'FastAPI', 'PostgreSQL', 'AWS ECS', 'Lambda', 'S3'],
            responsibilities: [
              'Architected the frontend using Next.js with a NestJS BFF (Backend for Frontend) layer, integrating GCP payment gateway for secure transactions processing',
              'Developed admin control systems (coupon management, image assets) with Next.js project structure, Python FastAPI backend, PostgreSQL, and AWS S3 with LocalStack for local development',
              'Engineered CI/CD pipelines using Express.js deploying to AWS ECS, ensuring zero-downtime deployments and automated testing workflows',
              'Implemented Python batch processing jobs running on AWS Lambda and ECS for scheduled data processing and report generation',
              'Enforced security best practices, performance optimization, and load testing to ensure platform reliability under high traffic conditions',
            ],
          },
          {
            name: 'Ride-Hailing Mobile App',
            description: 'Full-featured ride-hailing mobile application similar to Grab with real-time driver matching, GPS tracking, route optimization, fare calculation, and in-app payments.',
            teamSize: 5,
            technologies: ['React Native', 'Node.js', 'Google Maps API', 'Socket.IO', 'PostgreSQL', 'Redis'],
            responsibilities: [
              'Architected the mobile app with React Native for cross-platform (iOS & Android) deployment',
              'Integrated Google Maps API for real-time location tracking, geocoding, route calculation, and ETA estimation',
              'Built real-time driver-passenger matching system using Socket.IO and Redis pub/sub',
              'Developed fare calculation engine with dynamic pricing based on distance, time, and demand surge',
              'Implemented ride tracking with live map updates, driver location streaming, and trip status management',
              'Designed and built the backend API with Node.js handling ride requests, payments, and driver management',
            ],
          },
        ],
      },
      {
        company: 'TonyTech Co., LTD',
        role: 'Full Stack Developer',
        period: '11/2019 – 04/2023',
        location: 'Da Nang',
        projects: [
          {
            name: 'Property Rental Management',
            description: 'Developed and maintained a property rental management system serving the Australian real estate market, built with React frontend and Golang backend on PostgreSQL.',
            teamSize: 5,
            technologies: ['React', 'Golang', 'Express.js', 'PostgreSQL', 'REST API'],
            responsibilities: [
              'Built and scaled a Vietnamese social networking platform supporting 5,000+ active users, utilizing React, Express.js, and MySQL with optimized query performance',
              'Collaborated with cross-functional teams to implement feature requirements, conduct code reviews, and ensure adherence to coding standards and best practices',
            ],
          },
        ],
      },
    ],
    experienceVi: [
      {
        company: 'VNEXT Software',
        role: 'Lập trình viên Full Stack | Trưởng nhóm',
        period: '11/2023 – Hiện tại',
        location: 'Đà Nẵng',
        projects: [
          {
            name: 'TaaS (Travel as a Service) — Nền tảng Hàng không Lớn',
            description: 'Dẫn dắt nhóm 5 lập trình viên xây dựng và duy trì nền tảng đặt vé du lịch cho hãng hàng không lớn, xử lý đặt khách sạn và thuê xe quy mô lớn với tính khả dụng cao.',
            teamSize: 5,
            technologies: ['Next.js', 'NestJS', 'Python', 'FastAPI', 'PostgreSQL', 'AWS ECS', 'Lambda', 'S3'],
            responsibilities: [
              'Thiết kế frontend với Next.js cùng NestJS BFF, tích hợp cổng thanh toán GCP',
              'Phát triển hệ thống quản trị (quản lý coupon, hình ảnh) với Next.js, Python FastAPI, PostgreSQL, AWS S3 với LocalStack',
              'Xây dựng CI/CD pipeline triển khai lên AWS ECS, đảm bảo zero-downtime và tự động hóa testing',
              'Triển khai Python batch processing trên AWS Lambda và ECS cho xử lý dữ liệu và báo cáo',
              'Áp dụng bảo mật, tối ưu hiệu năng và load testing đảm bảo ổn định dưới tải cao',
            ],
          },
          {
            name: 'Ứng dụng Đặt xe',
            description: 'Ứng dụng đặt xe di động đầy đủ tính năng tương tự Grab, hỗ trợ ghép tài xế real-time, theo dõi GPS, tối ưu lộ trình, tính cước và thanh toán trong ứng dụng.',
            teamSize: 5,
            technologies: ['React Native', 'Node.js', 'Google Maps API', 'Socket.IO', 'PostgreSQL', 'Redis'],
            responsibilities: [
              'Thiết kế kiến trúc ứng dụng di động với React Native cho đa nền tảng (iOS & Android)',
              'Tích hợp Google Maps API cho theo dõi vị trí real-time, geocoding, tính toán lộ trình và ước tính thời gian đến',
              'Xây dựng hệ thống ghép tài xế-hành khách real-time sử dụng Socket.IO và Redis pub/sub',
              'Phát triển engine tính cước với giá động dựa trên khoảng cách, thời gian và nhu cầu',
              'Triển khai theo dõi chuyến đi với bản đồ live, streaming vị trí tài xế và quản lý trạng thái chuyến',
              'Thiết kế và xây dựng backend API với Node.js xử lý yêu cầu đặt xe, thanh toán và quản lý tài xế',
            ],
          },
        ],
      },
      {
        company: 'TonyTech Co., LTD',
        role: 'Lập trình viên Full Stack',
        period: '11/2019 – 04/2023',
        location: 'Đà Nẵng',
        projects: [
          {
            name: 'Quản lý Cho thuê Bất động sản',
            description: 'Phát triển và duy trì hệ thống quản lý cho thuê bất động sản phục vụ thị trường Úc, xây dựng với React frontend và Golang backend trên PostgreSQL.',
            teamSize: 5,
            technologies: ['React', 'Golang', 'Express.js', 'PostgreSQL', 'REST API'],
            responsibilities: [
              'Xây dựng và mở rộng nền tảng mạng xã hội Việt Nam hỗ trợ 5,000+ người dùng với React, Express.js, MySQL',
              'Hợp tác với nhóm đa chức năng triển khai yêu cầu, code review, đảm bảo tuân thủ coding standards',
            ],
          },
        ],
      },
    ],
    education: [
      {
        institution: 'Da Nang University of Science and Technology',
        degree: "Engineer's Degree, Information Technology",
        period: '2018 – 2023',
      },
    ],
    certificates: [
      { name: 'AWS Solutions Architect Associate', status: 'certified' },
      { name: 'AWS Developer Associate', status: 'certified' },
      { name: 'Best Employee 2024 — VNEXT', status: 'certified' },
      { name: 'Best Internship Award — TonyTech', status: 'certified' },
      { name: 'Gold Medal, Badminton — VNEXT', status: 'certified' },
    ],
    languages: [
      { name: 'Vietnamese', level: 'Native', levelVi: 'Bản ngữ', description: 'Native speaker', descriptionVi: 'Người bản ngữ' },
      { name: 'English', level: 'Professional (TOEIC 860)', levelVi: 'Chuyên nghiệp (TOEIC 860)', description: 'Professional working proficiency', descriptionVi: 'Trình độ làm việc chuyên nghiệp' },
    ],
    softSkills: ['Team Leadership', 'Mentoring & Training', 'Problem Solving', 'Cross-functional Collaboration', 'Technical Communication'],
    softSkillsVi: ['Lãnh đạo nhóm', 'Hướng dẫn & Đào tạo', 'Giải quyết vấn đề', 'Hợp tác đa chức năng', 'Giao tiếp kỹ thuật'],
    activities: ['Gaming', 'Tech Blogging', 'Film'],
    activitiesVi: ['Gaming', 'Blog công nghệ', 'Phim ảnh'],
    cvFile: '[CV_FILE_MEMBER_4]',
  },
  // TODO: Replace with actual Member 5 data when CV is provided
  // Avatar: [AVATAR_MEMBER_5]
  // Name: Replace "Coming Soon" with actual name
  // Role: Replace "Team Member" with actual role
  // Bio: Replace with actual bio
  // Experience: Add work experience data
  // Skills: Add technical skills
  // Projects: Add project data
  {
    id: 'member-5',
    name: 'Coming Soon',
    nameVi: 'Sắp ra mắt',
    role: 'Team Member',
    roleVi: 'Thành viên',
    email: '',
    location: '',
    avatar: '[AVATAR_MEMBER_5]',
    bio: 'Stay tuned for our next team member',
    bioVi: 'Hãy đón chờ thành viên tiếp theo của chúng tôi',
    skills: [],
    skillCategories: [],
    experience: [],
    education: [],
    certificates: [],
    languages: [],
    cvFile: '',
    isPlaceholder: true,
  },
];
